import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ppProcessorService from '../services/ppProcessorService';
import ConfirmModal from './shared/ConfirmModal';
import { lineDiff } from '../utils/lineDiff';
import './PaymentPorterPage.css';
import './PaymentPorterProcessorPage.css';
import { formatDateTime } from '../utils/datetime';

const statusBadge = (status) => {
    if (!status) return null;
    const s = (status || '').toUpperCase();
    const cls =
        s === 'RUNNING'                ? 'ppp-badge-running'   :
        s === 'COMPLETED'              ? 'ppp-badge-done'      :
        s === 'COMPLETED_WITH_ERRORS'  ? 'ppp-badge-failed'    :
        s === 'FAILED'                 ? 'ppp-badge-failed'    :
        s === 'INITIALISING'           ? 'ppp-badge-init'      :
        s === 'SUCCESS'                ? 'ppp-badge-success'   :
        s === 'ERROR'                  ? 'ppp-badge-error'     :
        s === 'SKIPPED'                ? 'ppp-badge-skipped'   :
        s === 'QUALIFIED'              ? 'ppp-badge-qualified' :
        s === 'DRY_RUN_QUALIFIED'      ? 'ppp-badge-dry'       : 'ppp-badge-init';
    const label = s === 'COMPLETED_WITH_ERRORS' ? 'ERRORS' : status;
    return <span className={`ppp-badge ${cls}`}>{label}</span>;
};

const fmtDate = (iso) => {
    if (!iso) return '—';
    return formatDateTime(iso);
};

// Sign-off on one error: what was done about it, by whom, and when.
//
// Stored on the record's own OpenSearch doc (checked_at / checked_by / checked_comment), so it inherits
// the 30-day pp-records retention and disappears with the error it describes rather than outliving it.
// `checked_by` comes from the caller's token in the lambda, never from this form — a resolution note is
// evidence of who looked, and a name typed into a box is not that.
const CheckModal = ({ record, saving, error, onSave, onClose }) => {
    const [comment, setComment] = useState(record?.checkedComment || '');
    const existing = !!record?.checkedAt;

    return (
        <div className="pp-modal-backdrop" onClick={onClose}>
            <div className="pp-modal ppp-check-modal" onClick={e => e.stopPropagation()}>
                <div className="pp-modal-header">
                    <span>{existing ? 'Update check' : 'Check error'}</span>
                    <button className="pp-modal-close" onClick={onClose} title="Close">×</button>
                </div>
                <div className="pp-modal-body">
                    <div className="ppp-check-context">
                        <div><strong>Confirmation</strong> {record?.confirmationNumber || '—'}</div>
                        <div><strong>Reservation</strong> {record?.reservationId || '—'}</div>
                        <div className="ppp-check-error">{record?.errorMessage || '—'}</div>
                    </div>

                    {existing && (
                        <div className="ppp-check-existing">
                            Checked by {record.checkedBy || 'unknown'} on {fmtDate(record.checkedAt)}
                        </div>
                    )}

                    <label className="ppp-check-label" htmlFor="ppp-check-comment">
                        What was done about this?
                    </label>
                    <textarea
                        id="ppp-check-comment"
                        className="ppp-check-textarea"
                        rows={5}
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        placeholder="e.g. Card re-entered on window 2 in Opera and the booking re-ported successfully."
                        autoFocus
                    />
                    <div className="ppp-check-hint">
                        {existing
                            ? 'Clearing the text and saving removes the check.'
                            : 'This marks the error as checked and counts towards the Checked figure on the run.'}
                    </div>

                    {error && <div className="pp-modal-error">{error}</div>}
                </div>
                {/* ppp-btn / ppp-btn-primary are the button classes this app actually defines —
                    pp-btn-secondary / pp-btn-primary do not exist anywhere, which is why these two
                    rendered as unstyled full-width browser buttons. */}
                <div className="pp-modal-footer ppp-check-footer">
                    <button className="ppp-btn" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="ppp-btn ppp-btn-primary" onClick={() => onSave(comment)} disabled={saving}>
                        {saving ? 'Saving…' : existing && !comment.trim() ? 'Remove check' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const PaymentPorterRunDetailPage = () => {
    const navigate = useNavigate();
    const { ppId, runUuid } = useParams();
    const { isSuperAdmin } = useAuth();

    const [checkRecordRow, setCheckRecordRow] = useState(null);   // record being annotated
    const [checkSaving, setCheckSaving]       = useState(false);
    const [checkError, setCheckError]         = useState(null);

    const [records, setRecords]       = useState(null);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);
    const [fileViewer, setFileViewer] = useState(null);
    const [fileLoading, setFileLoading] = useState(false);
    const [runningRow, setRunningRow] = useState(null);       // reservationId currently being queued
    const [runResults, setRunResults] = useState({});          // { [reservationId]: 'queued' | 'error' }
    const [confirmModal, setConfirmModal] = useState(null);
    const [search, setSearch]         = useState('');

    useEffect(() => {
        setLoading(true);
        ppProcessorService.getRunDetail(ppId, runUuid)
            .then(res => setRecords(res.data?.records || []))
            .catch(() => setError('Failed to load run detail'))
            .finally(() => setLoading(false));
    }, [ppId, runUuid]);

    const viewFile = async (rec, type) => {
        const title = type === 'before' ? 'Reservation (from OHIP)' : 'OHIP Update — Request & Response';
        setFileViewer({ title, data: null });
        setFileLoading(true);
        try {
            const res = await ppProcessorService.getReservationFile(ppId, rec.reservationId, runUuid, type);
            setFileViewer({ title, data: res.data?.data });
        } catch {
            setFileViewer({ title, data: { error: 'Failed to load file' } });
        } finally {
            setFileLoading(false);
        }
    };

    // Save (or clear) the annotation, then patch the row in place. Re-fetching the whole run to reflect
    // one comment would throw away the expanded Before/After state the user may have open.
    const handleSaveCheck = async (comment) => {
        if (!checkRecordRow) return;
        setCheckSaving(true);
        setCheckError(null);
        try {
            const res = await ppProcessorService.checkRecord(ppId, runUuid, checkRecordRow.reservationId, comment);
            const d = res.data || {};
            setRecords(prev => (prev || []).map(r => (
                r.reservationId === checkRecordRow.reservationId
                    ? { ...r, checkedAt: d.checkedAt || null, checkedBy: d.checkedBy || null, checkedComment: d.checkedComment || null }
                    : r
            )));
            setCheckRecordRow(null);
        } catch (err) {
            setCheckError(err.response?.data?.message || 'Failed to save the check');
        } finally {
            setCheckSaving(false);
        }
    };

    const handleRunNow = (rec) => {
        setConfirmModal({
            title: 'Re-run Reservation',
            message: `Re-process reservation ${rec.confirmationNumber || rec.reservationId} now? This will apply rule actions live in the PMS.`,
            confirmLabel: 'Run Now',
            onConfirm: async () => {
                setRunningRow(rec.reservationId);
                try {
                    await ppProcessorService.runSingleReservation(ppId, {
                        reservationId:      rec.reservationId,
                        confirmationNumber: rec.confirmationNumber,
                        ruleId:             rec.ruleId,
                        matchedCriteria:    rec.matchedCriteria,
                    });
                    setRunResults(prev => ({ ...prev, [rec.reservationId]: 'queued' }));
                    setTimeout(() => setRunResults(prev => {
                        const next = { ...prev };
                        delete next[rec.reservationId];
                        return next;
                    }), 3000);
                } catch (err) {
                    const msg = err.response?.data?.error?.message || err.response?.data?.message || 'Failed to queue';
                    setRunResults(prev => ({ ...prev, [rec.reservationId]: `error:${msg}` }));
                } finally {
                    setRunningRow(null);
                }
            },
        });
    };

    // Client-side filter across the visible columns (confirmation, reservation id, rule, status,
    // matched criteria, error/skip reason). Empty query shows everything.
    const q = search.trim().toLowerCase();
    const filtered = q && records
        ? records.filter(r => [
            r.confirmationNumber, r.reservationId, r.ruleName, r.status,
            (r.matchedCriteria || []).join(' '), r.errorMessage, r.skipReason,
          ].some(v => String(v ?? '').toLowerCase().includes(q)))
        : (records || []);

    return (
        <div className="pp-page" style={{ maxWidth: 1200 }}>
            <button className="pp-back-btn" onClick={() => navigate(`/apps/payment-porter/${ppId}`, { state: { tab: 'runs' } })}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Payment Porter
            </button>

            <div className="pp-header" style={{ marginBottom: '1.5rem' }}>
                <h1 className="pp-title">Run Detail</h1>
                <p className="pp-subtitle-text" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{runUuid}</p>
            </div>

            {loading && <div className="ppp-loading">Loading reservations…</div>}
            {error   && <div className="ppp-error">{error}</div>}
            {!loading && !error && records?.length === 0 && (
                <div className="ppp-empty">No reservation records found for this run.</div>
            )}

            {!loading && !error && records?.length > 0 && (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search reservation, confirmation, rule, status, criteria, error…"
                            style={{ flex: 1, maxWidth: 440, padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.85rem' }}
                        />
                        <span style={{ fontSize: '0.78rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{filtered.length} of {records.length}</span>
                    </div>
                    <div className="ppp-table-wrap">
                        <table className="ppp-table">
                            <thead>
                                <tr>
                                    <th>Confirmation</th>
                                    <th>Reservation ID</th>
                                    <th>Rule</th>
                                    <th>Status</th>
                                    <th>Matched Criteria</th>
                                    <th>Error / Skip</th>
                                    {/* The check is an annotation on the record, so it belongs in the
                                        table next to the error it explains — the button in the actions
                                        column only says THAT it was checked, never what was done. */}
                                    <th>Checked</th>
                                    <th>Updated</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r, i) => {
                                    const hasDebug  = isSuperAdmin && !!(r.reservationBefore || r.reservationAfter);
                                    const isOpen    = expandedRow === i;
                                    const isRunning = runningRow === r.reservationId;
                                    const runResult = runResults[r.reservationId];
                                    const runError  = typeof runResult === 'string' && runResult.startsWith('error:')
                                        ? runResult.slice(6) : null;
                                    return (
                                        <React.Fragment key={i}>
                                            <tr>
                                                <td>{r.confirmationNumber || '—'}</td>
                                                <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{r.reservationId || '—'}</td>
                                                <td style={{ fontSize: '0.78rem', color: '#6b7280' }}>{r.ruleName || '—'}</td>
                                                <td>{statusBadge(r.status)}</td>
                                                <td style={{ maxWidth: 240, fontSize: '0.75rem' }}>
                                                    {(r.matchedCriteria || []).join(', ') || '—'}
                                                </td>
                                                <td style={{ maxWidth: 180, fontSize: '0.75rem', color: r.errorMessage ? '#dc2626' : r.skipReason ? '#92400e' : undefined }}>
                                                    {r.errorMessage || r.skipReason || '—'}
                                                </td>
                                                <td style={{ maxWidth: 200, fontSize: '0.75rem' }}>
                                                    {r.checkedAt ? (
                                                        <div className="ppp-checked-cell">
                                                            <div className="ppp-checked-comment">{r.checkedComment || '—'}</div>
                                                            <div className="ppp-checked-meta">
                                                                {r.checkedBy || 'unknown'} · {fmtDate(r.checkedAt)}
                                                            </div>
                                                        </div>
                                                    ) : '—'}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.updatedAt)}</td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                                        {hasDebug && (
                                                            <button
                                                                className="ppp-detail-btn sa-only"
                                                                onClick={() => setExpandedRow(isOpen ? null : i)}
                                                            >
                                                                {isOpen ? 'Hide' : 'Before / After'}
                                                            </button>
                                                        )}
                                                        {(r.status || '').toUpperCase() === 'ERROR' && (
                                                            <button
                                                                className={`ppp-detail-btn${r.checkedAt ? ' ppp-detail-btn-checked' : ''}`}
                                                                onClick={() => { setCheckError(null); setCheckRecordRow(r); }}
                                                                title={r.checkedAt
                                                                    ? `Checked by ${r.checkedBy || 'unknown'} — ${r.checkedComment || ''}`
                                                                    : 'Record what was done about this error'}
                                                            >
                                                                <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{flexShrink:0}}>
                                                                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                </svg>
                                                                {r.checkedAt ? 'Checked' : 'Check'}
                                                            </button>
                                                        )}
                                                        {r.ruleId && (
                                                            <button
                                                                className={`ppp-detail-btn${runResult === 'queued' ? ' ppp-detail-btn-success' : runError ? ' ppp-detail-btn-error' : ' ppp-detail-btn-run'}`}
                                                                onClick={() => handleRunNow(r)}
                                                                disabled={isRunning || runResult === 'queued'}
                                                                title={runError || undefined}
                                                            >
                                                                {isRunning
                                                                    ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner" style={{flexShrink:0}}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg></>
                                                                    : runResult === 'queued' ? <><svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{flexShrink:0}}><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>Queued</>
                                                                    : runError ? 'Failed'
                                                                    : <><svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" style={{flexShrink:0}}><polygon points="1,0.5 9.5,5 1,9.5"/></svg>Run Now</>
                                                                }
                                                            </button>
                                                        )}
                                                    </div>
                                                    {runError && (
                                                        <div style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '0.25rem', maxWidth: 160 }}>{runError}</div>
                                                    )}
                                                </td>
                                            </tr>
                                            {isOpen && hasDebug && (
                                                <tr className="pp-logs-detail-row">
                                                    <td colSpan={9}>
                                                        {(r.reservationBefore && r.reservationAfter) ? (
                                                            <>
                                                                <div className="pp-detail-body-label" style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                                                    <span>Before → After — {r.status === 'ERROR' ? 'attempted change (not applied)' : 'changes the run applied'}</span>
                                                                    <span style={{ fontSize: '0.72rem' }}>
                                                                        <a onClick={() => viewFile(r, 'before')} style={{ cursor: 'pointer', color: '#4a6cf7' }}>↗ full before</a>
                                                                        <a onClick={() => viewFile(r, 'after')} style={{ cursor: 'pointer', color: '#4a6cf7', marginLeft: '0.75rem' }}>↗ full after</a>
                                                                    </span>
                                                                </div>
                                                                <pre className="pp-detail-body pp-detail-diff">
                                                                    {lineDiff(JSON.stringify(r.reservationBefore, null, 2), JSON.stringify(r.reservationAfter, null, 2)).map((l, idx) => (
                                                                        <div key={idx} className={l.t === 'add' ? 'pp-diff-add' : l.t === 'del' ? 'pp-diff-del' : 'pp-diff-same'}>
                                                                            <span className="pp-diff-gutter">{l.t === 'add' ? '+' : l.t === 'del' ? '-' : ' '}</span>{l.s}
                                                                        </div>
                                                                    ))}
                                                                </pre>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className="pp-detail-body-label" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                                                    <span>{r.reservationBefore ? 'Reservation at run time (no change captured)' : 'Update payload'}</span>
                                                                    <span style={{ fontSize: '0.72rem' }}>
                                                                        <a onClick={() => viewFile(r, r.reservationBefore ? 'before' : 'after')} style={{ cursor: 'pointer', color: '#4a6cf7' }}>↗ view full</a>
                                                                    </span>
                                                                </div>
                                                                <pre className="pp-detail-body">{JSON.stringify(r.reservationBefore || r.reservationAfter, null, 2)}</pre>
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: '1.5rem', color: '#6b7280' }}>No records match “{search}”.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {fileViewer && (
                        <div className="pp-logs-overlay" onClick={() => setFileViewer(null)}>
                            <div className="pp-file-viewer-dialog" onClick={e => e.stopPropagation()}>
                                <div className="pp-creds-modal-header">
                                    <span>{fileViewer.title}</span>
                                    <button className="pp-modal-close" onClick={() => setFileViewer(null)}>✕</button>
                                </div>
                                <div className="pp-file-viewer-body">
                                    {fileLoading || !fileViewer.data
                                        ? <div className="ppp-loading">Loading…</div>
                                        : <pre className="pp-file-viewer-json">{JSON.stringify(fileViewer.data, null, 2)}</pre>}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {confirmModal && (
                <ConfirmModal
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmLabel={confirmModal.confirmLabel}
                    onConfirm={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
                    onCancel={() => setConfirmModal(null)}
                />
            )}

            {checkRecordRow && (
                <CheckModal
                    record={checkRecordRow}
                    saving={checkSaving}
                    error={checkError}
                    onSave={handleSaveCheck}
                    onClose={() => { setCheckRecordRow(null); setCheckError(null); }}
                />
            )}
        </div>
    );
};

export default PaymentPorterRunDetailPage;
